import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import {
	Checkbox,
	FormControlLabel,
	Stack,
	TextField,
	Typography,
	Card,
	CardContent,
	CardHeader,
	CardActions,
	Divider,
} from "@mui/material";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import IconButton from "@mui/material/IconButton";
import { InfoWarningDeleteByDescriptionMessage } from "Infrastructure/Models/Messages/Messages";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { FormValue, areAllFieldsValid } from "Infrastructure/Utils/FormUtils";
import {
	DeviceForm,
	getActionTypeDescription,
} from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FunctionComponent, useState } from "react";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { v4 as uuidv4 } from "uuid";
import { ActionType } from "Infrastructure/Api/ApiCustom";

export type InfoWarningDeleteFormData = {
	isValidationShown: boolean;
	description: FormValue<string>;
	notifyClientByEmail: FormValue<boolean>;
};

export const getNewFormData = () => {
	return {
		isValidationShown: false,
		description: {
			value: "",
		},
		notifyClientByEmail: {
			value: false,
			isValid: true,
		},
	};
};

type Props = {
	deviceForm: DeviceForm;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onDelete: (message: InfoWarningDeleteByDescriptionMessage) => void;
	formData: InfoWarningDeleteFormData;
	setFormData: (data: InfoWarningDeleteFormData) => void;
};

const InfoWarningsActionDelete: FunctionComponent<Props> = (props) => {
	const {
		deviceForm,
		hubConnection,
		closeDialog,
		onDelete,
		formData,
		setFormData,
	} = props;
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [showSpaceTooltip, setShowSpaceTooltip] = useState(false);

	const isFormDirty = () => {
		const initial = getNewFormData();
		return Object.keys(formData).some((key) => {
			const val = (formData as any)[key]?.value;
			const initVal = (initial as any)[key]?.value;
			if (Array.isArray(val) && Array.isArray(initVal)) {
				return val.join() !== initVal.join();
			}
			return val !== initVal;
		});
	};

	const handleSubmit = () => {
		if (isFormValid()) {
			if (
				!hubConnection ||
				hubConnection.state !== HubConnectionState.Connected
			) {
				showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
				return;
			}
			const infoWarningDeleteMessage: InfoWarningDeleteByDescriptionMessage = {
				filteredDeviceNames: deviceForm.devices
					.filter((x) => x.isChecked)
					.map((x) => x.name),
				description: formData.description.value,
				notifyClientByEmail: formData.notifyClientByEmail.value ?? false,
				requestId: uuidv4(),
			};

			onDelete(infoWarningDeleteMessage);

			setFormData(getNewFormData());
		} else {
			setFormData({
				...formData,
				isValidationShown: true,
			});
		}
	};

	const handleTextFieldChange = (e: any) => {
		const { name, value } = e.target;
		const normalized = name === "description" ? value.replace(/\s/g, "") : value;
		setFormData({
			...formData,
			[name]: { value: normalized, isValid: isFieldValid(name, normalized) },
		});
	};
	const handleCheckboxChange = (e: any, checked: boolean) => {
		const { name } = e.target;
		setFormData({
			...formData,
			[name]: { value: !!checked, isValid: true },
		});
	};

	const isFieldValid = (name: string, value: any): boolean => {
		switch (name) {
			case "description":
				return !!value && value?.length > 0;
			default:
				return true;
		}
	};

	const isFormValid = () => areAllFieldsValid(formData);

	const emailTooltip = (
		<span>
			Na vybraných instancích musí být zapnuta feature{" "}
			<em>IsSendingInfoWarningNotificationsEnabled</em> a nastaven příjemce
			e-mailu.
		</span>
	);

	const getConfirmDialogContent = () => {
		let confirmContent = (
			<>Smazat infolištu bez odeslání notifikačního e-mailu?</>
		);
		if (
			!!formData.notifyClientByEmail.value &&
			deviceForm.devices
				.filter((x) => x.isChecked)
				.some((x) => x.deviceStatus.infoWarningNotificationEmailEnabled)
		) {
			const emailDevices = deviceForm.devices.filter(
				(x) => x.isChecked && x.deviceStatus.infoWarningNotificationEmailEnabled
			);

			confirmContent = (
				<>
					<span>
						Smazat a odeslat notifikaci o smazání infolišt na následující
						e-maily?
					</span>
					<ul>
						{emailDevices.map((d, _i) => (
							<li>
								<strong>{d.name}</strong>
								<ul>
									<li>
										{d.deviceStatus.infoWarningNotificationEmailRecipients}
									</li>
								</ul>
							</li>
						))}
					</ul>
				</>
			);
		}

		return confirmContent;
	};

	const formCanBeOpened = () => {
		if (deviceForm.devices.filter((x) => x.isChecked).length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return false;
		}
		return true;
	};

	return (
		<Card variant="outlined" style={{ boxShadow: "none" }}>
			<CardHeader
				title={
					<Typography variant="subtitle1">
						{getActionTypeDescription(
							ActionType.InfoWarningDeleteByDescription
						)}
					</Typography>
				}
				action={
					<IconButton
						edge="end"
						size="small"
						onClick={() => setFormData(getNewFormData())}
						disabled={!isFormDirty()}
						sx={{ mr: 1 }}
					>
						<ClearAllIcon />
					</IconButton>
				}
			/>
			<Divider />
			<CardContent sx={{ pb: 0 }}>
				<Stack spacing={2}>
					<Stack direction="row" spacing={1} alignItems="center">
						<TextField
							name="description"
							label="Identifikátor / popis"
							variant="outlined"
							size="small"
							fullWidth
							value={formData.description.value?.replace(/\s/g, "")}
							inputProps={{ maxLength: 100 }}
							onChange={handleTextFieldChange}
							error={
								formData.isValidationShown && !formData.description.isValid
							}
							onKeyDown={(e) => {
								if (e.key === " ") {
									e.preventDefault();
									setShowSpaceTooltip(true);
									setTimeout(() => setShowSpaceTooltip(false), 1500);
								}
							}}
						/>
						<FormControlLabel
							label="Zaslat notifikaci na e-mail"
							control={
								<Checkbox
									name="notifyClientByEmail"
									checked={formData.notifyClientByEmail.value ?? false}
									onChange={handleCheckboxChange}
								/>
							}
							sx={{ minWidth: 240 }}
						/>
					</Stack>
				</Stack>
			</CardContent>
			<CardActions sx={{ pt: 0, justifyContent: "flex-end" }}>
				<BlConfirmButton
					modalTitle={"Smazání"}
					modalContent={getConfirmDialogContent()}
					onResult={(result) => {
						if (!!result) {
							handleSubmit();
						}
					}}
					disabled={!isFormValid()}
					onClick={() => formCanBeOpened()}
				>
					Smazat
				</BlConfirmButton>
			</CardActions>
		</Card>
	);
};

export default InfoWarningsActionDelete;
